// Angular imports
import { Injectable } from '@angular/core';

// 3rd party imports
import moment from 'moment-timezone';
import { RRule, RRuleSet } from 'rrule';
import { DateTime } from 'luxon';

// Service imports
import { HelperService } from '../../../_services/shared-services/helper.service';

// Model imports
import { CalendarInstance } from '../../../_models/calendar-instance';
import { CalendarEvent } from '../../../_models/calendar-event';
import { CommunityCalendarResultDto } from '../../_models/community-calendar-result-dto';
import { AmenityCalendarResultDto } from '../../_models/amenity-calendar-result-dto';

// Constant imports
import {
  CalendarExportContants,
  inclusiveUntilForExport,
  makeUntilInclusive,
  RULE_AUTHORING_TZ
} from '@therealmanage/ciranet-ui-common';

@Injectable()
export class CalendarHelperService {
  constructor(private helperService: HelperService) {}

  export(
    fileName: string,
    data: CommunityCalendarResultDto[] | AmenityCalendarResultDto[],
    timezone: string = 'Etc/UTC'
  ): void {
    const instance = this.instance();
    this.addEvents(instance, data, timezone);
    const fileData = new Blob([instance.toCalendarString()], {
      type: 'text/calendar'
    });
    this.helperService.downloadFileUsingBlob(fileData, `${fileName}.ics`);
  }

  instance(): CalendarInstance {
    return new CalendarInstance('CiraNet Resident / Owner Portal');
  }

  addEvent(
    instance: CalendarInstance,
    dto: CommunityCalendarResultDto | AmenityCalendarResultDto,
    timezone: string = 'Etc/UTC'
  ): void {
    instance.addEvent(this.getCalendarEvent(dto, timezone));
  }

  addEvents(
    instance: CalendarInstance,
    dtos: CommunityCalendarResultDto[] | AmenityCalendarResultDto[],
    timezone: string = 'Etc/UTC'
  ): void {
    dtos.forEach(
      (dto: CommunityCalendarResultDto | AmenityCalendarResultDto) => {
        this.addEvent(instance, dto, timezone);
      }
    );
  }

  getCalendarEvent(
    dto: CommunityCalendarResultDto | AmenityCalendarResultDto,
    timezone: string = 'Etc/UTC'
  ): CalendarEvent {
    const event: CalendarEvent = new CalendarEvent();
    event.title = dto.subject;
    event.description = dto.description;
    event.createdDate = moment
      .tz(dto.addedOn, timezone)
      .format(CalendarExportContants.FORMAT_STRING);
    event.createdBy = { name: dto.scheduledBy };
    event.startDate = moment
      .tz(dto.startDate, timezone)
      .format(CalendarExportContants.FORMAT_STRING);
    event.endDate = moment
      .tz(dto.endDate, timezone)
      .format(CalendarExportContants.FORMAT_STRING);
    event.status = 'BUSY';
    event.uid = dto.appointmentId.toString();
    // FRNT-582: exported UNTIL is the admin's Central calendar day, end-of-day
    // inclusive, regardless of the viewer timezone threaded through other fields.
    event.rrule = dto.recurrenceRule
      ? inclusiveUntilForExport(dto.recurrenceRule)
      : dto.recurrenceRule;
    event.exdate = !!dto.recurrenceException
      ? this.convertExclusionDates(dto.recurrenceException, timezone)
      : dto.recurrenceException;
    event.allDay = dto.allDay;
    event.timezone = timezone;
    return event;
  }

  convertExclusionDates(
    dateValues: string,
    timezone: string = 'Etc/UTC'
  ): string {
    let dates = dateValues.split(',').map((date) => date.trim());
    dates = dates.map((date) =>
      moment
        .tz(date, CalendarExportContants.FORMAT_STRING, timezone)
        .format(CalendarExportContants.FORMAT_STRING)
    );
    return dates.join(',');
  }

  expandRecurrenceRule(
    calendarResults: CommunityCalendarResultDto[] | AmenityCalendarResultDto[]
  ): any[] {
    const allResults: any[] = [];

    calendarResults.forEach(
      (result: CommunityCalendarResultDto | AmenityCalendarResultDto) => {
        if (!!result.recurrenceRule) {
          const rruleSet = new RRuleSet();
          // Parse and Add recurrence rule.
          // The rule is authored in Central; the API start is UTC. Express the
          // start as its Central wall-clock, then anchor DTSTART as UTC-floating
          // (literal trailing 'Z', NO TZID). RES-659: with a TZID-anchored
          // DTSTART, rrule resolves an absolute, browser-zone-dependent instant
          // whose `.all()` UTC fields are offset-shifted — which the
          // re-interpretation below would then double-shift (recurring events
          // rendered hours off, depending on browser zone). A UTC-floating
          // DTSTART places the wall-clock verbatim into each occurrence's UTC
          // fields, so re-interpreting it in RULE_AUTHORING_TZ yields the correct
          // true instant on every browser.
          const dtStartText = moment
            .tz(result.startDate, RULE_AUTHORING_TZ)
            .format('YYYYMMDDTHHmmss');
          const ruleText = `DTSTART:${dtStartText}Z\nRRULE:${result.recurrenceRule}`;
          const options = RRule.parseString(ruleText);
          // FRNT-582: include the final date-ended day (was dropped by rrule's midnight cutoff)
          makeUntilInclusive(options, result.recurrenceRule);
          rruleSet.rrule(new RRule(options));

          // Calculating event duration using start and end difference in milliseconds
          const eventDuration = moment(result.endDate).diff(
            moment(result.startDate),
            'ms',
            true
          );
          // Expand all dates
          const eventDates = rruleSet.all();
          const events = eventDates.map((eventDate: Date) => {
            // RES-659: because DTSTART above is UTC-floating, each occurrence's
            // wall-clock lands verbatim in the Date's UTC fields. Re-interpret
            // those wall-clock parts in RULE_AUTHORING_TZ to get the TRUE UTC
            // instant — browser-zone independent and consistent with the
            // non-recurring API value (also a true UTC instant).
            const occurrenceWallClock = DateTime.fromJSDate(eventDate).toUTC();
            const eventStart = DateTime.fromObject(
              {
                year: occurrenceWallClock.year,
                month: occurrenceWallClock.month,
                day: occurrenceWallClock.day,
                hour: occurrenceWallClock.hour,
                minute: occurrenceWallClock.minute,
                second: occurrenceWallClock.second
              },
              { zone: RULE_AUTHORING_TZ }
            ).toJSDate();
            const eventEnd = new Date(eventStart.getTime() + eventDuration);
            const event = Object.assign({}, result, {
              startDate: eventStart,
              endDate: eventEnd
            });
            return event;
          });

          // Parse and remove recurrence exception dates from events
          if (!!result.recurrenceException) {
            const exceptions = result.recurrenceException
              .split(',')
              .map((exdate: string) => moment(exdate.trim()).toDate());
            allResults.push(
              ...events.filter((e) => {
                const index = exceptions.findIndex((ex) =>
                  moment(ex).isSame(moment(e.startDate), 'day')
                );
                return index === -1;
              })
            );
          } else {
            allResults.push(...events);
          }
        } else {
          allResults.push(result);
        }
      }
    );

    return allResults;
  }
}
